import React, { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { Menu, X, ChevronDown } from "lucide-react";
import fr from "@/content/fr";

const LOGO_URL =
  "/images/logohd.png";

// `kind: "scroll"` items live on the homepage (anchor scroll).
// `kind: "dropdown"` items reveal a small submenu of "route" links.
const links = [
  { id: "hero", label: fr.nav.home, kind: "scroll" },
  {
    id: "events",
    label: fr.nav.events,
    kind: "dropdown",
    children: [
      { id: "streetwear", label: fr.nav.streetwearTab, to: "/expo-streetwear-sneaker-culture" },
      { id: "beauty", label: fr.nav.beautyTab, to: "/beauty-wellness-weekend" },
    ],
  },
  { id: "editions", label: fr.nav.editions, kind: "scroll" },
  { id: "partners", label: fr.nav.partners, kind: "scroll" },
];

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [eventsOpen, setEventsOpen] = useState(false);
  const [mobileEventsOpen, setMobileEventsOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const eventsRef = useRef(null);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 32);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  // Close the desktop dropdown on outside click
  useEffect(() => {
    const onClick = (e) => {
      if (eventsRef.current && !eventsRef.current.contains(e.target)) {
        setEventsOpen(false);
      }
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  // Scroll-anchor navigation. Works from any page: if we're not on the
  // homepage, navigate there first and pass along which section to land on.
  const handleScrollLink = (id) => {
    setOpen(false);
    if (location.pathname !== "/") {
      navigate("/", { state: { scrollTo: id } });
      return;
    }
    setTimeout(() => {
      const el = document.getElementById(id);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 60);
  };

  const handleRouteLink = (to) => {
    setOpen(false);
    setEventsOpen(false);
    navigate(to);
  };

  const onLinkClick = (l) => {
    if (l.kind === "scroll") handleScrollLink(l.id);
    // "dropdown" kind is handled by its own toggle, not this generic handler
  };

  return (
    <>
      <header
        data-testid="navbar"
        className={`ma-cible fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
          scrolled
            ? "backdrop-blur-xl bg-[#15042E]/70 border-b border-white/[0.06]"
            : "bg-transparent"
        }`}
      >
        <div className="max-w-[1320px] mx-auto px-5 sm:px-8 lg:px-12 h-[68px] flex items-center justify-between">
          <Link
            to="/"
            className="flex items-center gap-2 group"
            data-testid="navbar-logo"
            aria-label="GEM Entertainment — Accueil"
          >
            <img
              src={LOGO_URL}
              alt="GEM Entertainment"
              className="h-9 w-auto select-none"
              draggable="false"
            />
          </Link>

          {/* Desktop nav */}
          <nav className="hidden md:flex items-center gap-7 lg:gap-9" aria-label="Navigation principale">
            {links.map((l) =>
              l.kind === "dropdown" ? (
                <div key={l.id} ref={eventsRef} className="relative">
                  <button
                    onClick={() => setEventsOpen((v) => !v)}
                    data-testid={`nav-link-${l.id}`}
                    aria-expanded={eventsOpen}
                    className="flex items-center gap-1.5 text-[13.5px] tracking-[0.14em] uppercase text-white/75 hover:text-white transition-colors"
                  >
                    {l.label}
                    <ChevronDown
                      size={14}
                      className={`transition-transform duration-200 ${eventsOpen ? "rotate-180" : ""}`}
                    />
                  </button>

                  <AnimatePresence>
                    {eventsOpen && (
                      <motion.div
                        initial={{ opacity: 0, y: 8 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: 8 }}
                        transition={{ duration: 0.18 }}
                        className="absolute left-1/2 -translate-x-1/2 top-[calc(100%+14px)] w-[240px] rounded-2xl p-2 bg-[#1F0A48]/95 backdrop-blur-xl border border-white/[0.10] shadow-[0_30px_80px_-20px_rgba(0,0,0,0.7)]"
                        data-testid="nav-dropdown-events"
                      >
                        {l.children.map((c) => (
                          <button
                            key={c.id}
                            onClick={() => handleRouteLink(c.to)}
                            data-testid={`nav-link-${c.id}`}
                            className="w-full text-left px-4 py-3 rounded-xl text-[13.5px] tracking-[0.08em] uppercase text-white/80 hover:text-white hover:bg-white/[0.06] transition-colors"
                          >
                            {c.label}
                          </button>
                        ))}
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              ) : (
                <button
                  key={l.id}
                  onClick={() => onLinkClick(l)}
                  data-testid={`nav-link-${l.id}`}
                  className="text-[13.5px] tracking-[0.14em] uppercase text-white/75 hover:text-white transition-colors relative group whitespace-nowrap"
                >
                  {l.label}
                  <span className="absolute left-0 -bottom-1 h-px w-0 bg-white/80 transition-all duration-300 group-hover:w-full" />
                </button>
              ),
            )}
          </nav>

          {/* CTA + mobile toggle */}
          <div className="flex items-center gap-3">
            <button
              onClick={() => handleScrollLink("partners")}
              data-testid="navbar-cta-partner"
              className="hidden sm:inline-flex items-center justify-center gap-2 h-10 px-5 rounded-full font-display font-bold uppercase tracking-[0.14em] text-[12.5px] text-white bg-[#E91E63] hover:bg-[#ff2974] transition-all shadow-[0_0_0_0_rgba(233,30,99,0.0)] hover:shadow-[0_0_36px_0_rgba(233,30,99,0.55)] hover:-translate-y-[1px]"
            >
              {fr.nav.cta}
            </button>

            <button
              type="button"
              onClick={() => setOpen((v) => !v)}
              className="md:hidden ma-masque-mobile inline-flex items-center justify-center w-10 h-10 rounded-full border border-white/15 text-white/85 hover:text-white hover:border-white/35 transition"
              aria-label={open ? fr.nav.menuClose : fr.nav.menuOpen}
              aria-expanded={open}
              aria-controls="mobile-menu"
              data-testid="navbar-mobile-toggle"
            >
              {open ? <X size={18} /> : <Menu size={18} />}
            </button>
          </div>
        </div>
      </header>

      {/* Mobile fullscreen overlay */}
      <AnimatePresence>
        {open && (
          <motion.div
            id="mobile-menu"
            data-testid="mobile-menu"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-40 md:hidden overflow-y-auto"
            style={{
              background:
                "radial-gradient(120% 80% at 20% 0%, #4A148C 0%, #2A0A52 55%, #15042E 100%)",
            }}
          >
            <nav className="min-h-full w-full flex flex-col items-start justify-center px-8 gap-6 py-24">
              {links.map((l, i) =>
                l.kind === "dropdown" ? (
                  <div key={l.id} className="w-full">
                    <motion.button
                      initial={{ x: -20, opacity: 0 }}
                      animate={{ x: 0, opacity: 1 }}
                      transition={{ delay: 0.05 + i * 0.06 }}
                      onClick={() => setMobileEventsOpen((v) => !v)}
                      data-testid={`mobile-nav-link-${l.id}`}
                      className="flex items-center gap-2 font-display text-white text-3xl sm:text-4xl font-extrabold uppercase tracking-tight text-left"
                    >
                      {l.label}
                      <ChevronDown
                        size={22}
                        className={`transition-transform duration-200 ${mobileEventsOpen ? "rotate-180" : ""}`}
                      />
                    </motion.button>
                    <AnimatePresence>
                      {mobileEventsOpen && (
                        <motion.div
                          initial={{ opacity: 0, height: 0 }}
                          animate={{ opacity: 1, height: "auto" }}
                          exit={{ opacity: 0, height: 0 }}
                          className="overflow-hidden pl-2"
                        >
                          {l.children.map((c) => (
                            <button
                              key={c.id}
                              onClick={() => handleRouteLink(c.to)}
                              data-testid={`mobile-nav-link-${c.id}`}
                              className="block mt-4 font-display text-white/75 text-xl font-bold uppercase tracking-tight text-left"
                            >
                              {c.label}
                            </button>
                          ))}
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                ) : (
                  <motion.button
                    key={l.id}
                    initial={{ x: -20, opacity: 0 }}
                    animate={{ x: 0, opacity: 1 }}
                    transition={{ delay: 0.05 + i * 0.06 }}
                    onClick={() => onLinkClick(l)}
                    data-testid={`mobile-nav-link-${l.id}`}
                    className="font-display text-white text-3xl sm:text-4xl font-extrabold uppercase tracking-tight text-left"
                  >
                    {l.label}
                  </motion.button>
                ),
              )}
              <motion.button
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.35 }}
                onClick={() => handleScrollLink("partners")}
                data-testid="mobile-nav-cta"
                className="mt-4 inline-flex items-center justify-center h-12 px-6 rounded-full bg-[#E91E63] hover:bg-[#ff2974] text-white font-display font-bold uppercase tracking-[0.14em] text-sm shadow-[0_0_40px_rgba(233,30,99,0.45)]"
              >
                {fr.nav.cta}
              </motion.button>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Navbar;
